import React, { 
  useState, 
  useRef, 
  useEffect, 
  ReactNode, 
  useContext, 
  createContext 
} from "react";

// 1. Context to manage state between Parent and Children
interface DropdownContextProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}

const DropdownContext = createContext<DropdownContextProps | undefined>(undefined);

interface DropdownProps {
  trigger: ReactNode; // This will be your Button component
  children: ReactNode; // These will be the menu items
  align?: "left" | "right";
}

export default function Dropdown({ trigger, children, align = "left" }: DropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <DropdownContext.Provider value={{ isOpen, setIsOpen }}>
      <div className="relative inline-block text-left" ref={containerRef}>
        {/* Trigger Area */}
        <div onClick={() => setIsOpen(!isOpen)}>
          {trigger}
        </div>

        {/* Dropdown Menu */}
        {isOpen && (
          <div
            className={`
              absolute z-50 mt-2 min-w-[160px] rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none
              ${align === "right" ? "right-0" : "left-0"}
            `}
          >
            <div className="py-1 flex flex-col">
              {children}
            </div>
          </div>
        )}
      </div>
    </DropdownContext.Provider>
  );
}

// 2. Helper Item Component to wrap your Buttons
export function DropdownItem({ children }: { children: ReactNode }) {
  const context = useContext(DropdownContext);
  
  // We wrap the child so that clicking any button inside automatically closes the dropdown
  return (
    <div 
      className="w-full" 
      onClick={() => context?.setIsOpen(false)}
    >
      {children}
    </div>
  );
}